import { useEffect, useRef, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { onIntroDone } from "../intro.js";
import "./ScreenMedia.css";

/**
 * A product screen: a looping HTML motion page from public/work/<slug>/
 * (runtime in public/work/_shared/screen.js), embedded like an image. Any
 * other src (a placeholder, a photo) still renders as a plain <img>.
 *
 * The iframe stays blank until the page-load intro is over and the screen is
 * near the viewport. Same-origin iframes share the main thread, so loading all
 * of them at once used to stutter the intro.
 *
 * "Near the viewport" is checked from the element's box on scroll and on every
 * ScrollTrigger refresh, not with an IntersectionObserver: after a back
 * navigation the page jumps straight to the remembered position while the
 * pinned cards are being re-laid out, and the observer's first (negative)
 * answer was never revised, leaving those screens blank.
 */
export default function ScreenMedia({ src, alt, lazy = true }) {
  const ref = useRef(null);
  const [live, setLive] = useState(false);
  const isScreen = src?.endsWith(".html");

  useEffect(() => {
    if (!isScreen || live) return;
    const reach = lazy ? 0.6 : 2;
    let frame = 0;
    let armed = false;
    const check = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const h = window.innerHeight;
      if (r.width && r.bottom > -h * reach && r.top < h * (1 + reach)) setLive(true);
    };
    const schedule = () => {
      if (armed && !frame) frame = requestAnimationFrame(check);
    };
    const off = onIntroDone(() => {
      armed = true;
      schedule();
    });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    ScrollTrigger.addEventListener("refresh", schedule);
    return () => {
      off();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      ScrollTrigger.removeEventListener("refresh", schedule);
    };
  }, [isScreen, live, lazy]);

  if (isScreen) {
    return <iframe ref={ref} className="screen-media" src={live ? src : undefined} title={alt} tabIndex={-1} />;
  }
  return <img className="screen-media" src={src} alt={alt} loading={lazy ? "lazy" : undefined} />;
}
